import { createStore } from 'vuex';
import axios from 'axios';

const apiUrl = process.env.NODE_ENV === 'development' 
  ? 'https://gentool-api.onrender.com/api'
  : 'https://gentool-api.onrender.com/api';

// Configure axios
axios.defaults.baseURL = apiUrl;
axios.defaults.withCredentials = true;

// Create a custom axios instance for auth requests
const authAxios = axios.create({
  baseURL: apiUrl,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor for debugging
axios.interceptors.request.use(request => {
  if (process.env.NODE_ENV === 'development') {
    console.log('Request:', {
      url: request.url,
      method: request.method,
      data: request.data
    });
  }
  return request;
});

const store = createStore({
  state() {
    return {
      user: JSON.parse(localStorage.getItem('user')) || null,
      token: localStorage.getItem('token') || null,
      users: [],
      services: [],
      formStructure: JSON.parse(localStorage.getItem('formStructure')) || null,
      isAuthChecked: false
    };
  },
  
  mutations: {
    setUser(state, user) {
      state.user = user;
      if (user) {
        localStorage.setItem('user', JSON.stringify(user));
      } else {
        localStorage.removeItem('user');
      }
    },
    setToken(state, token) {
      state.token = token;
      if (token) {
        localStorage.setItem('token', token);
        // Set token in axios headers for all instances
        [axios, authAxios].forEach(instance => {
          instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        });
      } else {
        localStorage.removeItem('token');
        // Remove token from axios headers for all instances
        [axios, authAxios].forEach(instance => {
          delete instance.defaults.headers.common['Authorization'];
        });
      }
    },
    setUsers(state, users) {
      state.users = users;
    },
    setFormStructure(state, structure) {
      state.formStructure = structure;
      if (structure) {
        localStorage.setItem('formStructure', JSON.stringify(structure));
      } else {
        localStorage.removeItem('formStructure');
      }
    },
    SET_SERVICES(state, services) {
      state.services = services;
    },
    setAuthChecked(state, value) {
      state.isAuthChecked = value;
    }
  },
  
  actions: {
    // Login action
    async login({ commit }, credentials) {
      const response = await authAxios.post('/login', credentials);
      
      if (response.status === 401) {
        return { 
          success: false, 
          message: 'Invalid username or password' 
        };
      }
      
      if (response.data?.token) {
        commit('setToken', response.data.token);
        commit('setUser', response.data.user);
        // Set token in axios headers immediately after login
        [axios, authAxios].forEach(instance => {
          instance.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
        });
        return { success: true, user: response.data.user };
      }
      
      return { 
        success: false, 
        message: 'Invalid response from server' 
      };
    },

    // Logout action
    async logout({ commit }) {
      try {
        await authAxios.post('/logout');
      } catch (error) {
        // Ignore logout errors
      } finally {
        commit('setUser', null);
        commit('setToken', null);
      }
    },

    // Check auth status
    async checkAuth({ state, commit }) {
      try {
        if (!state.token) {
          console.log('No token found in state');
          commit('setUser', null);
          commit('setToken', null);
          commit('setAuthChecked', true);
          return false;
        }

        // Set the token in headers before making the request
        axios.defaults.headers.common['Authorization'] = `Bearer ${state.token}`;
        
        const response = await axios.get('/check-auth');
        
        if (response.data.authenticated && response.data.user) {
          commit('setUser', response.data.user);
          commit('setAuthChecked', true);
          return true;
        }
        
        // If we get here, auth failed
        commit('setUser', null);
        commit('setToken', null);
        commit('setAuthChecked', true);
        return false;
      } catch (error) {
        console.error('Auth check failed:', error);
        if (error.response?.status === 401) {
          console.log('Token expired or invalid');
        }
        commit('setUser', null);
        commit('setToken', null);
        commit('setAuthChecked', true);
        return false;
      }
    },

    // Restore user management actions
    async fetchUsers({ commit }) {
      try {
        const response = await axios.get('/users');
        commit('setUsers', response.data);
      } catch (error) {
        throw error;
      }
    },

    async createUser({ commit }, userData) {
      try {
        // Ensure data is properly formatted
        const formattedData = {
          username: userData.username.trim(),
          password: userData.password,
          email: userData.email.trim(),
          is_admin: Boolean(userData.is_admin)
        };
        
        const response = await axios.post(
          '/users',
          formattedData,
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.data && response.data.user) {
          commit('ADD_USER', response.data.user);
        }
        
        return response.data;
      } catch (error) {
        console.error('Store createUser error:', error.response?.data || error);
        throw error;
      }
    },

    async updateUser({ commit }, { id, userData }) {
      try {
        const response = await axios.put(`/users/${id}`, userData);
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    async deleteUser({ commit }, id) {
      try {
        await axios.delete(`/users/${id}`);
      } catch (error) {
        throw error;
      }
    },

    async fetchSettings({ state }) {
      try {
        const response = await axios.get('/settings');
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch settings');
      }
    },

    async saveSettings({ state }, settingsData) {
      try {
        const response = await axios.post('/settings', settingsData);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to save settings');
      }
    },

    async fetchFormStructure({ commit }) {
      try {
        const response = await axios.get('/form-structure');
        commit('setFormStructure', response.data.structure);
        return response.data.structure;
      } catch (error) {
        console.error('Error fetching form structure:', error);
        throw new Error('Failed to fetch form structure');
      }
    },

    async saveFormStructure({ commit }, structure) {
      try {
        await axios.post('/form-structure', { structure });
        commit('setFormStructure', structure);
      } catch (error) {
        console.error('Error saving form structure:', error);
        throw new Error('Failed to save form structure');
      }
    },

    async fetchServices({ commit }) {
      try {
        const response = await axios.get('/services');
        commit('SET_SERVICES', response.data);
        return response.data;
      } catch (error) {
        console.error('Error fetching services:', error);
        throw error;
      }
    },

    async updateServices({ commit }, services) {
      try {
        await axios.put('/services', services.map(service => ({
          id: service.id,
          name: service.name,
          title: service.title,
          display_order: service.display_order
        })));
        commit('SET_SERVICES', services);
      } catch (error) {
        console.error('Error updating services:', error);
        if (error.response) {
          throw new Error(error.response.data.error || 'Failed to update services');
        } else if (error.request) {
          throw new Error('Network error - server may be offline');
        } else {
          throw new Error('Error setting up request');
        }
      }
    },

    async deleteService({ commit }, id) {
      try {
        const response = await axios.delete(`/services/${id}`);
        if (!response.data.success) {
          throw new Error('Failed to delete service');
        }
        return response.data;
      } catch (error) {
        console.error('Error deleting service:', error);
        throw new Error(error.response?.data?.error || 'Failed to delete service');
      }
    },

    async updateProfile({ commit }, { email, currentPassword, newPassword }) {
      try {
        console.log('Updating profile...', { email, hasNewPassword: !!newPassword });
        const response = await axios.put('/users/profile', {
          email,
          currentPassword,
          newPassword
        });
        
        commit('setUser', response.data.user);
        return response.data;
      } catch (error) {
        console.error('Profile update error:', error.response?.data);
        if (error.response?.status === 404) {
          throw { errors: { general: 'User not found' } };
        }
        if (error.response?.status === 400) {
          throw error.response.data;
        }
        throw error.response?.data || {
          errors: {
            general: 'Failed to update profile'
          }
        };
      }
    }
  },

  getters: {
    isAuthenticated: (state) => !!state.token && !!state.user,
    isAdmin: (state) => state.user?.is_admin === true || state.user?.is_admin === 1,
    isReady: (state) => state.isAuthChecked
  }
});

// Set initial auth header if token exists
if (store.state.token) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${store.state.token}`;
}

export default store; 